import { useState, useEffect, useRef } from "react";
import { Box, IconButton, Tooltip, keyframes, Typography, CircularProgress } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import MicOffIcon from "@mui/icons-material/MicOff";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { useTranslation } from "react-i18next";

// Sound wave bar animations
const soundBar1 = keyframes`
  0%, 100% {
    height: 4px;
  }
  50% {
    height: 18px;
  }
`;

const soundBar2 = keyframes`
  0%, 100% {
    height: 8px;
  }
  50% {
    height: 14px;
  }
`;

const soundBar3 = keyframes`
  0%, 100% {
    height: 10px;
  }
  50% {
    height: 22px;
  }
`;

const soundBar4 = keyframes`
  0%, 100% {
    height: 6px;
  }
  50% {
    height: 16px;
  }
`;

const soundBar5 = keyframes`
  0%, 100% {
    height: 9px;
  }
  50% {
    height: 20px;
  }
`;

const soundBar6 = keyframes`
  0%, 100% {
    height: 5px;
  }
  50% {
    height: 15px;
  }
`;

const soundBar7 = keyframes`
  0%, 100% {
    height: 7px;
  }
  50% {
    height: 19px;
  }
`;

const spinnerRotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

interface SummaryVoiceRecorderProps {
  onTranscriptChange: (transcript: string) => void;
  onAIImprove?: (transcript: string) => Promise<string>;
  language?: string;
  disabled?: boolean;
  maxLength?: number;
  currentText?: string;
  onRecordingChange?: (isRecording: boolean) => void;
  onFinalizingChange?: (isFinalizing: boolean) => void;
  onProcessingMessageChange?: (message: string) => void;
}

export const SummaryVoiceRecorder: React.FC<SummaryVoiceRecorderProps> = ({
  onTranscriptChange,
  onAIImprove,
  language = "en-US",
  disabled = false,
  maxLength,
  currentText = "",
  onRecordingChange,
  onFinalizingChange,
  onProcessingMessageChange,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const [isRecording, setIsRecording] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);
  const [processingMessage, setProcessingMessage] = useState("");
  const [isProcessingSpeech, setIsProcessingSpeech] = useState(false);

  // Notify parent of processing message changes
  useEffect(() => {
    onProcessingMessageChange?.(processingMessage);
  }, [processingMessage, onProcessingMessageChange]);
  const recognitionRef = useRef<any>(null);
  const isRecordingRef = useRef(false);
  const currentTextRef = useRef(currentText);
  const maxLengthRef = useRef(maxLength);
  const onTranscriptChangeRef = useRef(onTranscriptChange);
  const textBeforeRecordingRef = useRef(currentText);
  const finalizingTimeoutRef = useRef<any>(null);
  const processingTimeoutRef = useRef<any>(null);
  const isStoppingRef = useRef(false);

  // Keep refs updated
  useEffect(() => {
    currentTextRef.current = currentText;
  }, [currentText]);

  useEffect(() => {
    maxLengthRef.current = maxLength;
  }, [maxLength]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
    onRecordingChange?.(isRecording);
  }, [isRecording, onRecordingChange]);

  useEffect(() => {
    onFinalizingChange?.(isFinalizing);
  }, [isFinalizing, onFinalizingChange]);

  useEffect(() => {
    onTranscriptChangeRef.current = onTranscriptChange;
  }, [onTranscriptChange]);

  useEffect(() => {
    // Check if speech recognition is supported
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    // Initialize speech recognition only once
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language;

    recognition.onresult = (event: any) => {
      let final = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript + " ";
        }
      }

      // Only update with final results directly to the input
      if (final) {
        // Update text immediately
        const newText = currentTextRef.current + final;
        if (!maxLengthRef.current || newText.length <= maxLengthRef.current) {
          // Update the ref immediately to ensure subsequent results build on this
          currentTextRef.current = newText;
          onTranscriptChangeRef.current(newText);

          // Only hide finalizing during active recording, not during stop/finalize phase
          if (!isStoppingRef.current) {
            setIsFinalizing(false);
            if (finalizingTimeoutRef.current) {
              clearTimeout(finalizingTimeoutRef.current);
            }
          }
        }
      }
    };

    recognition.onerror = (event: any) => {
      // Handle expected errors silently
      if (event.error === "no-speech") {
        // User didn't speak, just continue
        return;
      }
      if (event.error === "aborted") {
        // Recognition was aborted, this is expected when stopping
        return;
      }

      // Log unexpected errors
      console.error("Speech recognition error:", event.error);
      setIsFinalizing(false);
      setIsProcessingSpeech(false);
      setProcessingMessage("");
      isStoppingRef.current = false;
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      setIsRecording(false);
    };

    recognition.onend = () => {
      if (isRecordingRef.current) {
        // Restart if still recording (for continuous mode)
        try {
          recognition.start();
          // Keep showing "Listening..." if restarting
          setProcessingMessage(t("Listening..."));
        } catch (e) {
          console.error("Error restarting recognition:", e);
          setIsRecording(false);
          setIsFinalizing(false);
          setIsProcessingSpeech(false);
          setProcessingMessage("");
          isStoppingRef.current = false;
        }
      } else if (isStoppingRef.current) {
        // Recognition ended after user stopped - wait for final results then cleanup
        if (finalizingTimeoutRef.current) {
          clearTimeout(finalizingTimeoutRef.current);
        }
        finalizingTimeoutRef.current = setTimeout(() => {
          setIsFinalizing(false);
          setIsProcessingSpeech(false);
          setProcessingMessage("");
          isStoppingRef.current = false;
        }, 800);
      } else {
        // Normal end, clear finalizing state
        setIsFinalizing(false);
        setIsProcessingSpeech(false);
        setProcessingMessage("");
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          console.error("Error aborting recognition:", e);
        }
      }
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, [language]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, []);

  const startRecording = () => {
    if (!recognitionRef.current || disabled) return;

    try {
      // Save the current text before starting recording
      currentTextRef.current = currentText;
      textBeforeRecordingRef.current = currentText;

      // Clear any previous timeouts
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }

      recognitionRef.current.start();
      setIsRecording(true);
      setIsFinalizing(false);
      setIsProcessingSpeech(false);
      setProcessingMessage(t("Listening..."));
    } catch (error) {
      console.error("Error starting recognition:", error);
      setProcessingMessage("");
    }
  };

  const stopRecording = () => {
    if (!recognitionRef.current) return;

    try {
      // Update state BEFORE stopping to prevent restart in onend handler
      setIsRecording(false);
      isRecordingRef.current = false;
      isStoppingRef.current = true;

      // Now stop the recognition
      recognitionRef.current.stop();

      // Show finalizing indicator while processing remaining speech
      setIsFinalizing(true);
      setIsProcessingSpeech(true);
      setProcessingMessage(t("voiceRecorderFinalizing"));

      // Safety timeout - prevent infinite loader (10 seconds max)
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
      finalizingTimeoutRef.current = setTimeout(() => {
        setIsFinalizing(false);
        setIsProcessingSpeech(false);
        setProcessingMessage("");
        isStoppingRef.current = false;
      }, 10000);
    } catch (error) {
      console.error("Error stopping recognition:", error);
      setIsFinalizing(false);
      setIsProcessingSpeech(false);
      setProcessingMessage("");
      isStoppingRef.current = false;
    }
  };

  const cancelRecording = () => {
    if (!recognitionRef.current) return;

    try {
      // Update state BEFORE stopping to prevent restart in onend handler
      setIsRecording(false);
      isRecordingRef.current = false;
      isStoppingRef.current = false;

      // Now stop the recognition
      recognitionRef.current.stop();

      setIsFinalizing(false);
      setIsProcessingSpeech(false);
      setProcessingMessage("");
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      // Revert to the text before recording started
      onTranscriptChangeRef.current(textBeforeRecordingRef.current);
    } catch (error) {
      console.error("Error canceling recognition:", error);
    }
  };

  const handleAIImprove = async () => {
    if (!onAIImprove || !currentText.trim()) return;

    try {
      setIsAIProcessing(true);

      // Save current text to undo stack before AI improvement
      setUndoStack((prev) => [...prev, currentText]);
      // Clear redo stack when new action is performed
      setRedoStack([]);

      // Add context for AI: "Improve the task title: {text}"
      const promptWithContext = `Improve the task title: "${currentText.trim()}"`;
      const improvedText = await onAIImprove(promptWithContext);

      // Apply max length constraint
      const finalText = improvedText || currentText;
      if (!maxLengthRef.current || finalText.length <= maxLengthRef.current) {
        onTranscriptChangeRef.current(finalText);
      } else {
        onTranscriptChangeRef.current(finalText.substring(0, maxLengthRef.current));
      }
    } catch (error) {
      console.error("Error improving text with AI:", error);
    } finally {
      setIsAIProcessing(false);
    }
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;

    // Get the last text from undo stack
    const previousText = undoStack[undoStack.length - 1];
    const newUndoStack = undoStack.slice(0, -1);

    // Save current text to redo stack
    setRedoStack((prev) => [...prev, currentText]);
    setUndoStack(newUndoStack);

    // Revert to previous text
    onTranscriptChangeRef.current(previousText);
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;

    // Get the last text from redo stack
    const nextText = redoStack[redoStack.length - 1];
    const newRedoStack = redoStack.slice(0, -1);

    // Save current text to undo stack
    setUndoStack((prev) => [...prev, currentText]);
    setRedoStack(newRedoStack);

    // Apply the next text
    onTranscriptChangeRef.current(nextText);
  };

  if (!isSupported) {
    return (
      <Tooltip title={t("Speech recognition not supported")}>
        <IconButton size="small" disabled>
          <MicOffIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, position: "relative" }}>
      {isRecording ? (
        <>
          {/* Cancel button */}
          <Tooltip title={t("Cancel recording")}>
            <IconButton
              onClick={cancelRecording}
              size="small"
              sx={{
                width: "24px",
                height: "24px",
                backgroundColor: (theme) => theme.palette.grey[400],
                color: "white",
                padding: 0,
                minWidth: "24px",
                zIndex: 2,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.grey[500],
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 16, fontWeight: "bold" }} />
            </IconButton>
          </Tooltip>
          {/* Save button */}
          <Tooltip title={t("Stop and save recording")}>
            <IconButton
              onClick={stopRecording}
              size="small"
              sx={{
                width: "24px",
                height: "24px",
                backgroundColor: (theme) => theme.palette.grey[600],
                color: "white",
                padding: 0,
                minWidth: "24px",
                zIndex: 2,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.grey[700],
                },
              }}
            >
              <CheckIcon sx={{ fontSize: 16, fontWeight: "bold" }} />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <>
          {/* Undo/Redo Group - Compact spacing */}
          {(undoStack.length > 0 || redoStack.length > 0) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.2,
                borderRight: "1px solid",
                borderColor: "divider",
                pr: 0.5,
                mr: 0.3,
              }}
            >
              {/* Undo Icon */}
              <Tooltip title={undoStack.length > 0 ? t("Undo") : t("No actions to undo")}>
                <span>
                  <IconButton
                    onClick={handleUndo}
                    disabled={undoStack.length === 0}
                    size="small"
                    sx={{
                      padding: "4px",
                      minWidth: 0,
                      color: (theme) =>
                        undoStack.length > 0
                          ? theme.palette.text.secondary
                          : theme.palette.action.disabled,
                      "&:hover": {
                        backgroundColor: (theme) =>
                          undoStack.length > 0
                            ? theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.08)"
                              : "rgba(0, 0, 0, 0.04)"
                            : "transparent",
                      },
                      "&.Mui-disabled": {
                        color: (theme) => theme.palette.action.disabled,
                      },
                    }}
                  >
                    <UndoIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>

              {/* Redo Icon */}
              <Tooltip title={redoStack.length > 0 ? t("Redo") : t("No actions to redo")}>
                <span>
                  <IconButton
                    onClick={handleRedo}
                    disabled={redoStack.length === 0}
                    size="small"
                    sx={{
                      padding: "4px",
                      minWidth: 0,
                      color: (theme) =>
                        redoStack.length > 0
                          ? theme.palette.text.secondary
                          : theme.palette.action.disabled,
                      "&:hover": {
                        backgroundColor: (theme) =>
                          redoStack.length > 0
                            ? theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.08)"
                              : "rgba(0, 0, 0, 0.04)"
                            : "transparent",
                      },
                      "&.Mui-disabled": {
                        color: (theme) => theme.palette.action.disabled,
                      },
                    }}
                  >
                    <RedoIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}

          {/* AI Improve Icon - always visible, disabled when no text */}
          {onAIImprove && (
            <Tooltip
              title={currentText.trim() ? t("AI Improve") : t("Add text to improve")}
              arrow
              placement="top"
            >
              <span>
                <IconButton
                  onClick={handleAIImprove}
                  disabled={!currentText.trim() || isAIProcessing}
                  size="small"
                  sx={{
                    padding: "4px",
                    minWidth: 0,
                    color: (theme) =>
                      currentText.trim()
                        ? theme.palette.primary.main
                        : theme.palette.action.disabled,
                    "&:hover": {
                      backgroundColor: (theme) =>
                        currentText.trim() ? "primary.50" : "transparent",
                    },
                    "&.Mui-disabled": {
                      color: (theme) => theme.palette.action.disabled,
                    },
                  }}
                >
                  {isAIProcessing ? (
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        border: (theme) =>
                          `2px solid ${theme.palette.primary.main}40`,
                        borderTop: (theme) =>
                          `2px solid ${theme.palette.primary.main}`,
                        borderRadius: "50%",
                        animation: `${spinnerRotate} 0.8s linear infinite`,
                      }}
                    />
                  ) : (
                    <AutoFixHighIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Voice Icon */}
          <Tooltip title={t("Start voice recording")}>
            <IconButton
              onClick={startRecording}
              disabled={disabled}
              size="small"
              sx={{
                padding: "4px",
                minWidth: 0,
                color: (theme) => theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <MicIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

export default SummaryVoiceRecorder;
